import { Navigate, useParams, Outlet } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import Breadcrumbs from './components/Breadcrumbs';
import {
  StepIndicator,
  Spinner,
  Button,
  useModal,
  StatusBadge,
  Alert,
} from '@gsa/afp-component-library';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setCurrentStep,
  setLeasingAccount,
  setLeasingSteps,
  setShipmentState,
  setShipmentLocation,
  setShipmentCountry,
  setReplacementVehicles,
  setRequisitionId,
  setSelectedStandardItem,
  setZoneData,
  setSelectedOptions,
  setSelectedColors,
  setSelectedContract,
  setSelectedDeliveryOption,
  setVehicleQuantity,
  setPaintAndGraphicsDescription,
  setCreatedByUserId,
  setMarshallingAddress,
  setAlternateDeliveryAddress,
  setDealershipDeliveryAddress,
  setRequisitionCustomerAccount,
  setBillingInformation,
  setRequisitionName,
  setRequisitionStatus,
  setViewMode,
  setRequisitionNumber,
  setDestinationType,
  setSelectedVehicleType,
  setSelectedVehicleSubType,
  setNonLowBidJustification,
  setReferralReplacement,
  setVehiclePurchaseType,
  setIsAreq,
  setIsPaintAndGraphicsDisabled,
  setCreatedByUserInfo,
  setAdditionalRequirements,
  setRequisitionAdditionalRequestId,
  setVendorQuotes,
} from '../../reducers/leasing.jsx';
import {
  LeasingSteps,
  InitialLeasingSteps,
  InitialLeasingAdditionalSteps,
  RequisitionViewMode,
  AreqStep,
} from './leasing-consts.js';
import SaveDraftModal from './components/save-draft/save-draft-modal.jsx';
import { RequisitionStatus } from '../ReviewDetails/RequisitionDetailsUtils';
import {
  useGetLeasingCustomerAccount,
  useGetLeasingReplacementVehicles,
  useGetLeasingRequisition,
  useGetVehicleByFilterWithZones,
  useGetZones,
} from '../../requests/leasing.jsx';
import {
  setAdditionalRequestId,
  setAdditionalsRequisitionState,
} from '../../reducers/additionals-reducer';
import useLeasingNavigation from '../../hooks/useLeasingNavigation.jsx';
import { NotFound } from '@gsa/afp-shared-ui-utils';
import { RequisitionStatusType } from '../my-requisitions/tableHelpers.jsx';
import './leasing-steps.scss';
import useSubmitLeasingRequisition from '../../hooks/useSubmitLeasingRequisition.jsx';
import {
  getRequisitionVehiclePurchaseType,
  isRequisitionWithCustomPaintOptions,
  updateLeasingSteps,
} from './leasing-utils.jsx';
import { useGetStandardItem } from '../../requests/common-requests.jsx';
import { useCurrentUser } from '@gsa/afp-shared-ui-utils';
import { useSystemAlert } from '../../services/system-alert.jsx';
import { REQUISITION_TYPE } from '../non-standard-purchase/constants.jsx';

export const getStep = (steps, step) => {
  const foundStepIndex = steps.findIndex((s) => s.key === step);

  if (foundStepIndex === -1) return null;

  return steps[foundStepIndex];
};

const LeasingStepsPage = () => {
  const [ready, setReady] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const params = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const { currentUser } = useCurrentUser();
  const pathEndsWith = location.pathname.replace(/\/$/, '').split('/').pop();
  const requisitionId = params.requisitionId;
  const { setSystemAlert, clearSystemAlert } = useSystemAlert();
  const {
    leasingSteps,
    currentStep,
    account,
    replacementVehiclesTableState,
    viewMode,
    requisitionName,
    requisitionStatus,
    requisitionNumber,
    selectedStandardItem,
    shipmentLocation,
  } = useSelector((state) => state.leasingReducer);
  const { isOpen, openModal, closeModal } = useModal();
  const { removeRequisitionIdFromUrl } = useLeasingNavigation();
  const [getCustomer, { loading: loadingCustomer }] =
    useGetLeasingCustomerAccount();
  const [getReplacementVehicles, { loading: loadingReplacementVehicles }] =
    useGetLeasingReplacementVehicles();
  const [getVehicleByFilter, { loading: loadingAccount }] =
    useGetVehicleByFilterWithZones();
  const [getStandardItemById] = useGetStandardItem(setSelectedStandardItem);
  const [error, setError] = useState(null);
  const [stack, setStack] = useState(null);

  let heading = {
    current: currentStep?.stepNumber,
    text: currentStep?.label,
  };

  useGetZones().then(({ data: zones }) => {
    dispatch(setZoneData(zones));
  });

  useEffect(() => {
    if (
      currentStep?.key &&
      currentStep?.key !== pathEndsWith &&
      Object.values(LeasingSteps).includes(pathEndsWith)
    ) {
      const step = getStep(leasingSteps, pathEndsWith);
      if (step) {
        dispatch(setCurrentStep(step));
      } else {
        dispatch(
          setCurrentStep(getStep(leasingSteps, LeasingSteps.REVIEW_SUBMIT)),
        );
      }
    }
  }, [location.pathname]);

  const { determineRequisitionViewMode } = useSubmitLeasingRequisition();

  useGetLeasingRequisition(requisitionId)
    .then(async ({ data: requisition }) => {
      clearSystemAlert();
      let validSteps = [...InitialLeasingSteps];
      if (requisition?.additionalRequestId) {
        validSteps = [...InitialLeasingAdditionalSteps];
      }
      const requisitionType = requisition?.requisitionType;
      const isAreqType = requisitionType === REQUISITION_TYPE.AREQ;
      if (
        requisition?.clientData?.clientState?.selectedContract
          ?.isLowBidContract === false &&
        !isAreqType
      ) {
        const nonLowBidJustification = {
          stepNumber: 4,
          key: LeasingSteps.NON_LOW_PRICE_JUSTIFICATION,
          label: 'Non-low price justification',
          status: 'not completed',
        };

        validSteps = [
          ...validSteps.slice(0, 3),
          nonLowBidJustification,
          ...validSteps.slice(3).map((step) => ({
            ...step,
            stepNumber: step.stepNumber + 1,
          })),
        ];
      }
      if (isAreqType) {
        dispatch(setIsAreq(true));
        validSteps = updateLeasingSteps(AreqStep, validSteps, 3);
      }
      if (requisition?.additionalRequestId) {
        dispatch(setAdditionalRequestId(requisition.additionalRequestId));
        dispatch(setAdditionalsRequisitionState(requisition));
      }
      const isPaintAndGraphicsStepExists = validSteps.some(
        (step) => step.key === LeasingSteps.PAINT_AND_GRAPHICS,
      );
      const selectedOptions =
        requisition?.clientData?.selectedOptions?.selectedOptions;
      const paintAndGraphicsDescription =
        requisition?.clientData?.clientState?.paintAndGraphicsDescription;
      const isRequisitionCPT =
        selectedOptions?.length > 0 &&
        isRequisitionWithCustomPaintOptions(
          selectedOptions,
          paintAndGraphicsDescription,
        );
      if (!isRequisitionCPT && isAreqType) {
        dispatch(setIsPaintAndGraphicsDisabled(true));
        validSteps = validSteps
          .filter((step) => step.key !== LeasingSteps.PAINT_AND_GRAPHICS)
          .map((step, index) => ({
            ...step,
            stepNumber: index + 1,
          }));
      } else {
        if (!isPaintAndGraphicsStepExists && isAreqType) {
          dispatch(setIsPaintAndGraphicsDisabled(false));
          const pAndGStep = {
            stepNumber: 5,
            key: LeasingSteps.PAINT_AND_GRAPHICS,
            label: 'Paint and Graphics',
            status: 'not completed',
          };
          validSteps = updateLeasingSteps(pAndGStep, validSteps, 4);
        }
      }

      let stepIndicator = getStep(validSteps, pathEndsWith);
      const clientState = requisition?.clientData?.clientState;
      const selectedOptionsFromDraft =
        requisition?.clientData?.selectedOptions?.selectedOptions || [];
      if (pathEndsWith === requisition?.requisitionId) {
        if (
          requisition?.requisitionStatus !== RequisitionStatus.DRAFT &&
          currentUser?.id !== requisition?.updatedBy
        ) {
          stepIndicator = getStep(validSteps, LeasingSteps.REVIEW_SUBMIT);
        } else {
          stepIndicator = getStep(validSteps, clientState?.currentStep?.key);
        }
      }

      let steps = validSteps.map((step) => {
        if (stepIndicator?.stepNumber === step?.stepNumber) {
          return { ...step, status: 'current' };
        } else if (stepIndicator?.stepNumber > step?.stepNumber) {
          return { ...step, status: 'completed' };
        }
        return step;
      });

      const viewMode = determineRequisitionViewMode(requisition);
      dispatch(setViewMode(viewMode));

      if (!requisition && requisitionId) {
        // removeRequisitionIdFromUrl();
      }
      if (selectedOptionsFromDraft?.length > 0) {
        const deliveryOption = selectedOptionsFromDraft.find(
          (option) => option.isDeliveryOption,
        );
        if (deliveryOption) {
          dispatch(setSelectedDeliveryOption([deliveryOption]));
        }
      }
      dispatch(setReferralReplacement(requisition?.referralReplacement));
      dispatch(
        setRequisitionAdditionalRequestId(requisition?.additionalRequestId),
      );
      dispatch(setRequisitionName(requisition?.friendlyName));
      dispatch(setRequisitionNumber(requisition?.requisitionNumber));
      dispatch(setCreatedByUserId(requisition?.createdBy));
      dispatch(setCreatedByUserInfo(requisition?.createdByInfo));
      dispatch(setRequisitionStatus(requisition?.requisitionStatus));
      dispatch(setLeasingSteps(steps));
      dispatch(
        setDestinationType({
          type: clientState?.destinationType,
          loadedFromDraft: true,
        }),
      );
      dispatch(setShipmentState(clientState?.selectedState || 'Select state'));
      dispatch(setShipmentLocation(clientState?.shipmentLocation));
      dispatch(
        setShipmentCountry(clientState?.shipmentCountry || 'Select country'),
      );
      dispatch(setSelectedVehicleType(clientState?.selectedVehicleType));
      dispatch(setSelectedVehicleSubType(clientState?.selectedVehicleSubType));
      dispatch(setNonLowBidJustification(clientState?.nonLowBidJustification));
      dispatch(setCurrentStep(stepIndicator));
      dispatch(setSelectedOptions(selectedOptionsFromDraft));
      dispatch(
        setAdditionalRequirements(
          requisition?.additionalRequirements?.sort((a, b) => {
            if (a.createdAt < b.createdAt) return -1;
            if (a.createdAt > b.createdAt) return 1;
            return 0;
          }),
        ),
      );
      dispatch(
        setPaintAndGraphicsDescription(
          clientState?.paintAndGraphicsDescription,
        ),
      );
      dispatch(
        setSelectedColors([
          {
            color: {
              value: requisition?.vehicles[0]?.makeColorCode,
              label: requisition?.vehicles[0]?.makeColorName,
              price: requisition?.vehicles[0]?.makeColorPriceToCustomer,
              vendorPrice: requisition?.vehicles[0]?.makeColorPriceToGsa,
            },
            quantity: requisition?.quantity,
          },
        ]),
      );
      dispatch(setMarshallingAddress(clientState?.marshallingAddress));
      dispatch(
        setAlternateDeliveryAddress(clientState?.alternateDeliveryAddress),
      );
      dispatch(
        setDealershipDeliveryAddress(clientState?.dealershipDeliveryAddress),
      );

      if (clientState?.selectedContract && requisition?.optionalEquipmentRate) {
        clientState.selectedContract.leaseRates.optionalEquipmentRate =
          requisition.optionalEquipmentRate;
      }

      if (clientState?.selectedContract && requisition?.optionalEquipmentRate) {
        clientState.selectedContract.leaseRates.optionalEquipmentRate =
          requisition.optionalEquipmentRate;
      }

      dispatch(setSelectedContract(clientState?.selectedContract));
      if (requisition) {
        dispatch(
          setBillingInformation({
            requisitionNumber: requisition?.requisitionNumber,
            agencyOrderNumber: requisition?.agencyOrderNumber,
            finSignalCode: requisition?.finSignalCode,
            finFundCode: requisition?.finFundCode,
            agency: {
              agencyCode: requisition?.agencyCode,
              agencyName: requisition?.agencyInfo?.name,
            },
            bureau: {
              bureauCode: requisition?.bureauCode,
              bureauName: requisition?.bureauInfo?.name,
            },
            office: {
              officeCode: requisition?.officeCode,
              officeName: requisition?.officeInfo?.officeName,
            },
          }),
        );
        dispatch(setVehicleQuantity(requisition?.quantity));
        dispatch(setRequisitionId(requisition.requisitionId));
        dispatch(setVendorQuotes(requisition.vendorQuotes || []));
        dispatch(
          setRequisitionCustomerAccount(
            requisition?.requisitionLeasingCustomerAccount,
          ),
        );

        if (requisition.additionalRequestId) {
          getCustomer({
            variables: {
              customerId: parseFloat(
                requisition.additionalRequest?.customerId || '',
              ),
            },
          })
            .then(({ data: customer }) => {
              const purchaseType = getRequisitionVehiclePurchaseType(customer);
              dispatch(setVehiclePurchaseType(purchaseType));
              dispatch(
                setLeasingAccount({
                  customer,
                  primaryPoC: {
                    fullName:
                      customer?.primaryContactAssociation?.pointOfContact
                        .pocFullName,
                    email:
                      customer?.primaryContactAssociation?.pointOfContact
                        .pocEmailAddress,
                    primaryPhone:
                      customer?.primaryContactAssociation?.pointOfContact
                        .pocPhone1,
                    primaryPhoneExt:
                      customer?.primaryContactAssociation?.pointOfContact
                        .pocPhone1Ext,
                    secondaryPhone: null,
                  },
                }),
              );
            })
            .catch((error) => {
              console.error(error);
              setError(error);
              setStack(error.stack);
            })
            .finally(() => {
              setReady(true);
            });
        } else {
          getReplacementVehicles({
            variables: {
              requisitionId: requisition.requisitionId,
              order: replacementVehiclesTableState.order,
              limit: replacementVehiclesTableState.limit,
              offset:
                (replacementVehiclesTableState.currentPage - 1) *
                replacementVehiclesTableState.limit,
            },
          })
            .then(({ data }) => {
              const replacementVehicles = data.rows;
              if (replacementVehicles?.length > 0) {
                dispatch(setReplacementVehicles(replacementVehicles));
                // get leasing account
                getVehicleByFilter({
                  variables: {
                    filters: [
                      {
                        operator: '$and',
                        conditions: [
                          {
                            key: 'id',
                            operator: '$exact',
                            value: replacementVehicles[0].assetId,
                          },
                        ],
                      },
                    ],
                  },
                })
                  .then(({ data: account }) => {
                    dispatch(setLeasingAccount(account));
                    const purchaseType =
                      getRequisitionVehiclePurchaseType(account);
                    dispatch(setVehiclePurchaseType(purchaseType));
                    setReady(true);
                  })
                  .catch((error) => {
                    console.error(error);
                    setError(error);
                    setStack(error.stack);
                    setReady(true);
                  });
              } else {
                setReady(true);
              }
            })
            .catch((error) => {
              console.error(error);
              setError(error);
              setStack(error.stack);
              setReady(true);
            });
        }
      } else {
        setReady(true);

        if (requisitionId) {
          setNotFound(true);
        }
      }

      let step = steps.find((step) => step.status === 'current');
      if (step?.key === LeasingSteps.COMPARE_AND_SELECT) {
        dispatch(setSelectedStandardItem(clientState?.selectedStandardItem));
      } else if (clientState?.selectedStandardItem?.id) {
        getStandardItemById({
          variables: {
            filter: {
              operator: 'EQ',
              key: 'id',
              value: clientState.selectedStandardItem.id,
            },
          },
        });
      }
    })
    .catch((error) => {
      console.error(error);
      setError(error);
      setStack(error.stack);
      setReady(true);
    })
    .finally(() => {
      setReady(true);
    });

  const handleSaveDraft = () => {
    openModal();
  };

  const loading =
    (leasingSteps?.length === 0 || !currentStep?.key || !ready) &&
    !(
      requisitionStatus === RequisitionStatus.ORDER_RECEIVED ||
      requisitionStatus === RequisitionStatusType.RECEIVED_BY_GSA
    );

  if (!ready) {
    if (
      !requisitionId &&
      !Object.values(LeasingSteps).some((step) => pathEndsWith === step)
    ) {
      return <Navigate to="/lease" />;
    }
  } else if (requisitionId && pathEndsWith === requisitionId) {
    if (
      requisitionStatus === RequisitionStatus.ORDER_RECEIVED ||
      requisitionStatus === RequisitionStatusType.RECEIVED_BY_GSA
    ) {
      return (
        <Navigate
          to={`/lease/${requisitionId}/${LeasingSteps.REVIEW_SUBMIT}`}
          replace
        />
      );
    } else {
      return (
        <Navigate
          to={`/lease/${requisitionId}/${
            currentStep?.key || LeasingSteps.REPLACEMENT_VEHICLES
          }`}
          replace
        />
      );
    }
  }

  const hasDealerTag =
    selectedStandardItem?.vehicleTypeCode?.tags?.value?.includes('DEALER');

  const requisitionStatusBadge = requisitionStatus
    ? RequisitionStatusType[requisitionStatus] || {
        color: 'Warning-Gray',
        label: `INVALID STATUS (${value})`,
      }
    : null;

  if (error) {
    return (
      <div className="margin-bottom-5">
        <section
          className="usa-header mobile-lg:grid-col-6"
          data-testid="access-denied-page-header"
        >
          <h1 className="font-sans-1xl">Error loading requisition</h1>
          <p className="font-body-lg">
            Please try again later. If you continue to experience technical
            difficulties with this page, please contact the GSA Fleet Technical
            Support team at fleet.helpdesk@gsa.gov or 866-472-6711 from 8:00
            a.m. - 7:00 p.m. ET, Monday-Friday.
          </p>
        </section>
        {window.AFP_CONFIG.showGraphqlErrors && (
          <Alert type="error" heading="Uncaught error">
            <pre>
              <code>{stack}</code>
            </pre>
          </Alert>
        )}
      </div>
    );
  }

  if (notFound) {
    return (
      <NotFound
        goBackLink={{
          href: '/lease',
          text: 'Go back to lease requisitions start',
        }}
      />
    );
  }

  return (
    <>
      <div>
        <div className="leasing-top-section">
          <Breadcrumbs />
        </div>
        {loading ? (
          <Spinner size="large" className="margin-y-5" />
        ) : (
          <>
            <div className="leasing-requisition-header">
              {viewMode !== RequisitionViewMode.EDIT ? (
                <div className="requisition-heading">
                  <h1>{requisitionName}</h1>
                  {requisitionStatusBadge && (
                    <StatusBadge variant={requisitionStatusBadge?.color}>
                      {requisitionStatusBadge?.label}
                    </StatusBadge>
                  )}
                </div>
              ) : (
                <div className="requisition-heading-edit-wrapper">
                  {hasDealerTag &&
                    currentStep.key === LeasingSteps.COMPARE_AND_SELECT && (
                      <Alert type="info" className="dealer-delivery-alert">
                        This order is for{' '}
                        <strong>
                          {account?.customer?.customerAgency?.id}-
                          {account?.customer?.customerAgency?.name}/
                          {account?.customer?.customerBureau?.id}-
                          {account?.customer?.customerBureau?.name}
                        </strong>{' '}
                        to be shipped to <strong>{shipmentLocation}</strong>.
                        <br />
                        Dealership delivery is standard for this vehicle. Other
                        delivery options may be available under the Delivery
                        option section.
                      </Alert>
                    )}

                  <div className="requisition-heading-edit">
                    <h1>Build a requisition </h1>
                    {requisitionNumber && (
                      <p>
                        <strong>{requisitionNumber}</strong>
                      </p>
                    )}
                    <p>
                      Use this feature to take the request approved by a GSA
                      leasing customer to create a requisition that becomes a
                      vehicle order.
                    </p>
                  </div>
                  <div>
                    {!requisitionId && !!account && (
                      <Button
                        label="Save draft"
                        variant="outline"
                        onClick={handleSaveDraft}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
            {viewMode === RequisitionViewMode.EDIT && (
              <StepIndicator
                className="leasing-step-indicator"
                counterSize="big"
                heading={heading}
                steps={leasingSteps}
              />
            )}
            <Outlet />
          </>
        )}
      </div>
      <SaveDraftModal isOpen={isOpen} onClose={closeModal} />
    </>
  );
};

export default LeasingStepsPage;
